import { useState, useEffect, useRef } from "react";
import { P, FF, inputBase } from "../../styles/tokens";
import { CATS_LIST } from "../../constants/categories";
import AccountSelect from "./AccountSelect";
import MoneyInput from "./MoneyInput";

export default function ExtrasInputRow({ row, onChange, onDelete, cuentas }) {
  const [local, setLocal] = useState(row);
  const [confirming, setConfirming] = useState(false);
  const dirty = useRef(false);
  const localRef = useRef(local);
  localRef.current = local;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (row.id !== local.id) {
      setLocal(prev => ({ ...prev, id: row.id }));
    }
    if (!dirty.current) setLocal(row);
  }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const forceCommit = () => {
      if (dirty.current) {
        dirty.current = false;
        onChangeRef.current(localRef.current);
      }
    };
    window.addEventListener("beforeunload", forceCommit);
    return () => {
      forceCommit();
      window.removeEventListener("beforeunload", forceCommit);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = (next) => { dirty.current = true; setLocal(next); };

  const commit = () => {
    if (!dirty.current) return;
    dirty.current = false;
    onChange(local);
  };

  const handleKey = (e) => { if (e.key === "Enter") e.target.blur(); /* blur triggers commit */ };

  // For selects, commit immediately
  const editAndCommit = (next) => { dirty.current = false; onChange(next); setLocal(next); };

  const todayStr = new Date().toISOString().slice(0, 10);
  const isDirty = local.concepto !== row.concepto || local.monto !== row.monto || local.cat !== row.cat || local.cuentaId !== row.cuentaId || local.fecha !== row.fecha;

  const catData = CATS_LIST.find((c) => c.id === local.cat);

  return (
    <div style={{ padding: "10px 0", borderBottom: `1px solid ${P.border}`, opacity: row.esRecurrente ? 0.8 : 1 }}>
      {/* Línea 1: icono + concepto + monto + acciones */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {row.esRecurrente && <span style={{ fontSize: 11, flexShrink: 0 }}>🔁</span>}
        <div
          style={{
            flexShrink: 0, width: 28, height: 28, borderRadius: 10,
            background: catData ? `${catData.color}18` : P.bg,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
          }}
        >
          {catData?.icon || "📦"}
        </div>
        <input
          aria-label="Concepto del gasto extra"
          value={local.concepto}
          onChange={(e) => edit({ ...local, concepto: e.target.value })}
          onBlur={commit}
          onKeyDown={handleKey}
          placeholder="Descripción…"
          style={{ flex: 1, border: "none", background: "transparent", color: P.text, fontSize: 14, fontFamily: FF, outline: "none", padding: "2px 0", fontWeight: 500, minWidth: 0 }}
        />
        <div style={{ position: "relative", width: 100, flexShrink: 0 }}>
          <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: P.textXs, fontSize: 11, pointerEvents: "none" }}>$</span>
          <MoneyInput
            aria-label="Monto del gasto extra"
            value={local.monto}
            onChange={(v) => edit({ ...local, monto: v })}
            onBlur={commit}
            onKeyDown={handleKey}
            style={{ ...inputBase, paddingLeft: 22, fontWeight: 700, textAlign: "right", paddingRight: 8, fontSize: 13 }}
          />
        </div>
        {isDirty && (
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={commit}
            style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid #10B981`, background: "#D1FAE5", color: "#065F46", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 700 }}
          >
            ✓
          </button>
        )}
        {confirming ? (
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#991B1B" }}>¿Seguro?</span>
            <button onClick={() => { onDelete(); setConfirming(false); }}
              aria-label="Confirmar eliminar"
              style={{ width: 36, height: 36, borderRadius: 10, border: "1.5px solid #FECACA", background: "#FEF2F2", color: "#DC2626", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✓</button>
            <button onClick={() => setConfirming(false)}
              aria-label="Cancelar"
              style={{ width: 36, height: 36, borderRadius: 10, border: "1.5px solid #E5E7EB", background: "#F3F4F6", color: "#6B7280", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
          </div>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${P.border}`, background: "transparent", color: P.textXs, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            ×
          </button>
        )}
      </div>
      {/* Línea 2: categoría + cuenta + fecha */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, paddingLeft: row.esRecurrente ? 24 : 36 }}>
        <select
          aria-label="Categoria del gasto"
          value={local.cat || ""}
          onChange={(e) => editAndCommit({ ...local, cat: e.target.value })}
          style={{
            ...inputBase, flex: 1, minWidth: 80, fontSize: 12, fontWeight: 500,
            color: catData ? catData.color : P.textSm,
            background: catData ? `${catData.color}0D` : P.bg,
            borderColor: catData ? `${catData.color}40` : P.border,
            padding: "5px 10px",
          }}
        >
          <option value="">Categoría…</option>
          {CATS_LIST.map((c) => (
            <option key={c.id} value={c.id}>{c.icon} {c.id}</option>
          ))}
        </select>
        <AccountSelect value={local.cuentaId} onChange={(v) => editAndCommit({ ...local, cuentaId: v })} cuentas={cuentas} required />
        <input
          aria-label="Fecha del gasto"
          type="date"
          value={local.fecha || todayStr}
          onChange={(e) => editAndCommit({ ...local, fecha: e.target.value })}
          style={{ ...inputBase, width: 130, fontSize: 11, padding: "5px 6px", color: P.textSm }}
        />
      </div>
    </div>
  );
}
